import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Layout";
import { PageHeader, Breadcrumbs, Overline } from "@/components/Bits";
import GuideBody from "@/components/GuideBody";
import api from "@/lib/api";
import { Clock, FileText, ArrowRight, Info, ExternalLink } from "lucide-react";

const CAT_LABELS = {
  "aprender-aleman": "Aprender alemán", "trabajo": "Trabajo", "vivienda": "Vivienda",
  "primeros-pasos": "Primeros pasos", "vida-cotidiana": "Vida cotidiana",
  "cultura-laboral": "Cultura laboral", "aleman-suizo": "Alemán suizo",
};

export function Guides() {
  const [guides, setGuides] = useState([]);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/guides/categories").then(({ data }) => setCats(data));
    api.get("/guides").then(({ data }) => { setGuides(data); setLoaded(true); });
  }, []);
  const filtered = active ? guides.filter((g) => g.category === active) : guides;
  return (
    <>
      <Seo title="Guías para vivir y trabajar en Suiza" description="Guías prácticas revisadas para hispanohablantes: alemán, trabajo, vivienda, primeros pasos y cultura laboral en Suiza." />
      <PageHeader overline="Vivir y trabajar en Suiza" title="Guías prácticas revisadas"
        subtitle="Contenido útil para hispanohablantes. Cada guía pasa por revisión humana antes de publicarse. La información es general y puede cambiar; consulta siempre el organismo oficial competente." />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: "Guías" }]} />
        <div className="max-w-3xl mb-8 space-y-3 text-[15px] leading-relaxed text-muted-foreground">
          <p>El alemán y la vida en Suiza vienen con muchas preguntas prácticas: cómo empadronarte en tu municipio, qué seguro médico necesitas, cómo entender un contrato de alquiler o desenvolverte en tu primer día de trabajo. En esta sección reunimos guías claras, en español, pensadas para hispanohablantes que ya viven en Suiza o están planeando el traslado.</p>
          <p>Cada guía explica el tema paso a paso, con el vocabulario alemán que necesitarás y enlaces a los organismos oficiales suizos correspondientes. La información es orientativa y puede cambiar con el tiempo; por eso indicamos siempre dónde verificarla. Usa los filtros por categoría para encontrar rápido lo que buscas.</p>
        </div>
        <div className="flex flex-wrap gap-2 mb-8">
          <button onClick={() => setActive(null)} className={`px-4 py-1.5 rounded-full text-sm font-medium border ${!active ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-secondary"}`}>Todas</button>
          {cats.map((c) => <button key={c} onClick={() => setActive(c)} className={`px-4 py-1.5 rounded-full text-sm font-medium border ${active === c ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-secondary"}`}>{CAT_LABELS[c] || c}</button>)}
        </div>
        {loaded && filtered.length === 0 && (
          <div className="rounded-lg border border-dashed border-border p-10 text-center">
            <FileText size={28} className="mx-auto text-muted-foreground mb-3" />
            <h3 className="font-display font-semibold text-lg">Guías en preparación</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">Nuestras guías se están preparando y revisando. No publicamos contenido sin revisión humana. Vuelve pronto.</p>
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((g) => (
            <Link key={g.slug} to={`/guias/${g.slug}`} data-testid={`guide-${g.slug}`}
              className="group rounded-lg border border-border bg-card overflow-hidden hover:-translate-y-1 hover:border-primary/40 transition-transform">
              {g.image && <img src={g.image} alt={g.image_alt || g.title} loading="lazy" className="w-full aspect-[3/2] object-cover border-b border-border" />}
              <div className="p-6">
                <span className="text-xs uppercase tracking-[0.15em] text-accent">{CAT_LABELS[g.category] || g.category}</span>
                <h2 className="font-display font-semibold text-lg mt-2">{g.title}</h2>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{g.summary}</p>
                <div className="mt-4 flex items-center gap-1 text-xs text-muted-foreground"><Clock size={12} />{g.reading_min} min de lectura</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </>
  );
}

export function Article() {
  const { slug } = useParams();
  const [g, setG] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/guides/${slug}`).then(({ data }) => setG(data)).catch(() => setErr(true)); }, [slug]);
  if (err) return <PageHeader title="Guía no disponible" subtitle="Esta guía todavía no está publicada o no existe." />;
  if (!g) return <div className="min-h-[50vh] flex items-center justify-center text-muted-foreground">Cargando...</div>;
  return (
    <>
      <Seo title={g.meta_title || g.title} description={g.meta_description} />
      <article className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: "Guías", to: "/guias" }, { label: g.title }]} />
        <GuideBody guide={g} showAds />

        <div className="mt-10 rounded-lg bg-primary text-primary-foreground p-6">
          <h3 className="font-display font-bold text-xl">Pon en práctica lo aprendido</h3>
          <Link to="/prueba-de-nivel"><Button variant="secondary" className="mt-4 rounded-full">Haz la prueba de nivel <ArrowRight size={16} className="ml-1" /></Button></Link>
        </div>
      </article>
    </>
  );
}
